import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Layers, Lock, ArrowRight } from "lucide-react";

const LOGIN_BG =
  "https://static.prod-images.emergentagent.com/jobs/c4840e87-a4c7-46bd-9d8d-eb19340bf291/images/2b2f84422774407228682d8a5fd14c0ba2b77959f51c4fc2c44f3b766be9a5f1.png";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const startGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      <div className="hidden lg:block relative overflow-hidden border-r border-zinc-200">
        <img
          src={LOGIN_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-white via-white/40 to-transparent" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-md bg-[#0A52FF] text-white">
              <Layers className="w-4 h-4" />
            </span>
            <div className="leading-tight">
              <div className="font-display font-extrabold tracking-tight">Makrolog AG</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">QR Code Studio</div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Internes Werkzeug</div>
            <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
              Erzeuge gebrandete QR-Codes — präzise &amp; markenkonform.
            </h1>
            <p className="mt-5 text-zinc-600 leading-relaxed">
              Texte, URLs, WLAN, Kontakte, E-Mail und mehr. Mit Farben, Logos und einer öffentlichen API für die Integration in eure Systeme.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 text-xs">
              {[
                ["7 Typen", "Text, URL, WLAN, …"],
                ["PNG &amp; SVG", "Pixelgenau · skalierbar"],
                ["Public API", "100 req/min · GET"],
              ].map(([h, s]) => (
                <div key={h} className="border border-zinc-200 rounded-md p-3 bg-white">
                  <div className="font-display font-bold" dangerouslySetInnerHTML={{ __html: h }} />
                  <div className="text-zinc-500 mt-0.5" dangerouslySetInnerHTML={{ __html: s }} />
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-zinc-500 font-mono">qr.makrolog.page</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <span className="grid place-items-center w-8 h-8 rounded-md bg-[#0A52FF] text-white">
              <Layers className="w-4 h-4" />
            </span>
            <div className="font-display font-extrabold tracking-tight">Makrolog AG</div>
          </div>

          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-2">Anmelden</div>
          <h2 className="font-display text-3xl font-bold tracking-tight">Willkommen zurück.</h2>
          <p className="mt-3 text-sm text-zinc-600">
            Anmeldung ausschließlich mit deiner <span className="font-mono font-medium text-zinc-900">@makrolog.de</span>-E-Mail über Google.
          </p>

          <Button
            onClick={startGoogleAuth}
            className="mt-8 w-full h-11 bg-[#0A52FF] hover:bg-[#003ECC] text-white text-sm font-medium gap-2"
            data-testid="google-login-btn"
          >
            <GoogleIcon className="w-4 h-4" />
            Mit Google anmelden
            <ArrowRight className="w-4 h-4 ml-auto" />
          </Button>

          <div className="mt-6 flex items-start gap-2 text-xs text-zinc-500 border border-zinc-200 rounded-md p-3 bg-zinc-50">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Nur E-Mail-Adressen mit der Domain <span className="font-mono">@makrolog.de</span> werden akzeptiert. Andere Konten werden automatisch abgelehnt.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.2C29.3 35 26.8 36 24 36c-5.1 0-9.5-3.3-11.2-7.9l-6.5 5C9.4 39.6 16.1 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.3l6.3 5.2c-.4.4 6.4-4.7 6.4-14.5 0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}
